import React, { useState } from 'react';
import { Clock, Check, ShoppingBag } from 'lucide-react';
import { EARTH, TYPOGRAPHY } from '../theme/constants';
import { CONFIG } from '../config';
import { LicenseKeyInput } from './LicenseKeyInput';

export function TrialBadge({ daysLeft }) {
  if (daysLeft === undefined || daysLeft === null) return null;
  const isUrgent = daysLeft <= 3;
  const isExpired = daysLeft === 0;
  const label = isExpired ? 'Trial expired' : `${daysLeft} day${daysLeft !== 1 ? 's' : ''} left`;

  return (
    <div
      className="flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium transition-colors"
      style={{
        backgroundColor: isExpired ? EARTH.terracotta[100] : isUrgent ? EARTH.terracotta[50] : EARTH.sage[50],
        color: isExpired ? EARTH.terracotta[700] : isUrgent ? EARTH.terracotta[600] : EARTH.sage[700],
        border: `1px solid ${isExpired ? EARTH.terracotta[300] : isUrgent ? EARTH.terracotta[200] : EARTH.sage[200]}`,
      }}
    >
      <Clock className="w-3 h-3" />
      <span>{label}</span>
    </div>
  );
}

export function TrialExpiredGate({ onLicenseActivated }) {
  const [showLicenseInput, setShowLicenseInput] = useState(false);

  const handleBuyNow = () => {
    window.open(CONFIG.CHECKOUT_URL, '_blank');
    // After buying, user will get a license key via email — show the input
    setShowLicenseInput(true);
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 9999,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      backgroundColor: 'rgba(26, 26, 26, 0.7)', backdropFilter: 'blur(8px)',
      fontFamily: TYPOGRAPHY.body,
    }}>
      <div style={{
        backgroundColor: EARTH.cream, borderRadius: '16px', padding: '48px',
        maxWidth: '520px', width: '90%', textAlign: 'center',
        boxShadow: '0 25px 50px rgba(0,0,0,0.25)',
        border: `1px solid ${EARTH.stone[200]}`,
      }}>
        <div style={{
          width: '64px', height: '64px', borderRadius: '50%',
          backgroundColor: EARTH.terracotta[100], display: 'flex',
          alignItems: 'center', justifyContent: 'center', margin: '0 auto 24px',
        }}>
          <Clock style={{ width: '32px', height: '32px', color: EARTH.terracotta[500] }} />
        </div>

        <h2 style={{
          fontFamily: TYPOGRAPHY.display, fontSize: '28px',
          color: EARTH.ink, marginBottom: '12px', textTransform: 'lowercase',
        }}>
          your trial has ended
        </h2>

        <p style={{ color: EARTH.stone[600], fontSize: '15px', lineHeight: '1.6', marginBottom: '32px' }}>
          Thanks for exploring arrangementLab! Get lifetime access for a one-time payment —
          no subscriptions, no recurring charges.
        </p>

        {/* Buy Now button */}
        <button
          onClick={handleBuyNow}
          style={{
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
            width: '100%', padding: '14px 24px', borderRadius: '12px',
            fontSize: '17px', fontWeight: 700,
            backgroundColor: EARTH.terracotta[500], color: '#fff', border: 'none',
            cursor: 'pointer', fontFamily: TYPOGRAPHY.body,
            transition: 'background-color 0.2s',
            marginBottom: '16px',
          }}
          onMouseEnter={(e) => e.target.style.backgroundColor = EARTH.terracotta[600]}
          onMouseLeave={(e) => e.target.style.backgroundColor = EARTH.terracotta[500]}
        >
          <ShoppingBag style={{ width: '20px', height: '20px' }} />
          Get Access — ${CONFIG.LAUNCH_PRICE}
          <span style={{ fontSize: '13px', fontWeight: 400, opacity: 0.8, textDecoration: 'line-through', marginLeft: '4px' }}>
            ${CONFIG.FULL_PRICE}
          </span>
        </button>

        {/* Divider */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: '12px',
          margin: '20px 0',
        }}>
          <div style={{ flex: 1, height: '1px', backgroundColor: EARTH.stone[200] }} />
          <span style={{ color: EARTH.stone[400], fontSize: '13px' }}>already purchased?</span>
          <div style={{ flex: 1, height: '1px', backgroundColor: EARTH.stone[200] }} />
        </div>

        {/* License key input */}
        {showLicenseInput ? (
          <div style={{ textAlign: 'left' }}>
            <LicenseKeyInput onActivated={onLicenseActivated} />
            <p style={{ color: EARTH.stone[400], fontSize: '12px', textAlign: 'center', marginTop: '8px' }}>
              Check your email for the license key from Polar.
            </p>
          </div>
        ) : (
          <button
            onClick={() => setShowLicenseInput(true)}
            style={{
              background: 'none', border: 'none', cursor: 'pointer',
              color: EARTH.terracotta[500], fontSize: '14px', fontWeight: 600,
              fontFamily: TYPOGRAPHY.body, textDecoration: 'underline',
              textUnderlineOffset: '3px',
            }}
          >
            Enter license key
          </button>
        )}

        {/* Docs link */}
        <div style={{ marginTop: '24px', paddingTop: '16px', borderTop: `1px solid ${EARTH.stone[200]}` }}>
          <a
            href="/help"
            target="_blank"
            rel="noopener noreferrer"
            style={{
              fontSize: '13px', color: EARTH.stone[400], textDecoration: 'none',
              fontFamily: TYPOGRAPHY.body, fontWeight: 500,
            }}
          >
            See what's inside arrangementLab →
          </a>
        </div>
      </div>
    </div>
  );
}
